import moment from "moment";
import { Order } from "../../models/Order";
import { ShoppingCart, ShoppingCartItem } from "../../models/ShoppingCart";

const formatMoney = (money: number) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
    }).format(money);
}

const formatDate = (isoDate: Date) => {
    if (!isoDate) {
        return ""
    }

    const date = new Date(isoDate);

    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
}

const formatUtcDateToLocal = (isoDate: Date, includeTime: boolean = false) => {
    if (!isoDate) {
        return ""
    }

    const date = moment.utc(isoDate).local();

    if (includeTime) {
        return date.format('MM/DD/yyyy hh:mm:ss A');
    }

    return date.format('MM/DD/yyyy');
}

const getFormatedFileSizeString = (fileSize: number, decimals?: number) => {
    if (fileSize && fileSize > 0) {
        const k = 1024
        const dm = !decimals ? 2 : decimals
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

        const i = Math.floor(Math.log(fileSize) / Math.log(k))

        if (i > 1) {
            return `${parseFloat((fileSize / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
        } else {
            return `${Math.ceil((fileSize / Math.pow(k, i)))} ${sizes[i]}`
        }

    }
    else {
        return "";
    }
}

const isCrossLink = (fieldDefinition) => {
    return fieldDefinition.isOrderId ||
        fieldDefinition.isRepairId ||
        fieldDefinition.isAssetId ||
        fieldDefinition.isInvoiceId;
}

const calculateTotalPrice = (items: ShoppingCartItem[]) => {

    return items.filter(x => x.Available).reduce((acc, val) => acc + val.Amount * val.PricePerUnit, 0);
}

const calculateTotalOldPrice = (items: ShoppingCartItem[]) => {

    return items.filter(x => x.Available).reduce((acc, val) => acc + val.Amount * val.OldPricePerUnit, 0);
}

function calculateCartTotalQuantity(cartItems: ShoppingCartItem[]) {
    return cartItems.reduce((acc, val) => acc + val.Amount, 0);
}

function calculateGrandTotalQuantity(carts: ShoppingCart[]) {
    return carts.reduce((acc, val) => acc + val.TotalQuantity, 0);
}

const containsDetails = (itemDetails, fieldName) => {
    let containsDetail = false;

    if (itemDetails && itemDetails.length > 0 && fieldName) {
        for (let i = 0; i < itemDetails.length; i++) {
            const properties = Object.keys(itemDetails[i]);

            if (properties && properties.length > 0) {
                for (let j = 0; j < properties.length; j++) {
                    if (properties[j].toLowerCase() === fieldName.toLowerCase()) {
                        containsDetail = true;
                        break;
                    }
                }
            }

            if (containsDetail) {
                break;
            }
        }
    }

    return containsDetail;
}

const getItemDetailsHoverText = (itemDetails, fieldName) => {
    let hoverTextProperty = Object.keys(itemDetails).find((p) => p.toLowerCase() === "hoverText".toLowerCase());
    if (hoverTextProperty && itemDetails[hoverTextProperty]) {
        let hoverTextValueProperty = Object.keys(itemDetails[hoverTextProperty]).find((p) => p.toLowerCase() === fieldName.toLowerCase());
        if (hoverTextValueProperty) {
            return itemDetails[hoverTextProperty][hoverTextValueProperty];
        }
    } else {
        return null;
    }
}

const sortShoppingCartsByName = (carts: ShoppingCart[]) => {
    carts.sort((a, b) => {
        const nameA = a.CartName.toUpperCase(); // ignore upper and lowercase
        const nameB = b.CartName.toUpperCase(); // ignore upper and lowercase
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }

        // names must be equal
        return 0;
    })
}

// Sort by published date (descending), then by title (ascending)
const sortShoppingCartsByModifiedDateThenName = (carts: ShoppingCart[]) => {
    const sorted = [...carts].sort((a, b) => {
        const dateDiff = b.CartModified.getTime() - a.CartModified.getTime();
        return dateDiff !== 0 ? dateDiff : a.CartName.toUpperCase().localeCompare(b.CartName.toUpperCase());
    });
}

export const buildOrdersCsvAndDownload = function (dataItems: Order[], filename) {

    let csvContent = '';
    const cleanRegex = new RegExp(',', 'g');

    csvContent = 'PartnerName,OrderNumber,Date,CartName,MFR PN,Description,QTY,Cost,Total';
    csvContent += '\n';

    // build rows for items
    dataItems.forEach((order) => {
        const partnerName = order.PartnerName ? order.PartnerName : "n/a";
        const orderNumber = order.Number ? order.Number : "n/a";
        const createdDate = order.CreatedDate ? order.CreatedDate : "n/a";
        const cartName = order.CartName ? order.CartName : "n/a";
        
        order.Items.forEach((item) => {
            let csvRow = '';
            const productNumber = item.ProductNumber ? item.ProductNumber : "n/a";
            const productName = item.ProductName ? item.ProductName : "n/a";
            const quantity = item.Quantity ? item.Quantity : 0;
            const price = item.Price > 0 ? formatMoney(item.Price) : "$--.--";
            const total = item.Price > 0 ? formatMoney(item.Price * item.Quantity) : "$--.--";

            csvRow += partnerName.replace(cleanRegex, ' ') + ","
                + orderNumber.replace(cleanRegex, ' ') + ","
                + createdDate + ","
                + cartName.replace(cleanRegex, ' ') + ","
                + productNumber.replace(cleanRegex, ' ') + ","
                + productName.replace(cleanRegex, ' ') + ","
                + quantity + ","
                + price + ","
                + total;

            csvContent += csvRow + '\n';
        })
    });

    downloadCsv(csvContent, filename);
}

// The download function takes a CSV string, the filename and mimeType as parameters
// Scroll/look down at the bottom of this snippet to see how download is called

export const downloadCsv = function (content, fileName) {
    var a = document.createElement('a');
    const mimeType = 'text/csv;encoding:utf-8';

    content = content.replace(/[^\x00-\x7F]/g, "");
    console.log('content', content);

    if (navigator.msSaveBlob) { // IE10
        navigator.msSaveBlob(new Blob([content], {
            type: mimeType
        }), fileName);
    } else if (URL && 'download' in a) { //html5 A[download]
        a.href = URL.createObjectURL(new Blob([content], {
            type: mimeType
        }));
        a.setAttribute('download', fileName);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    } else {
        document.location.href = 'data:application/octet-stream,' + encodeURIComponent(content); // only this mime type is supported
    }
}

export {
    formatMoney,
    formatDate,
    getFormatedFileSizeString,
    isCrossLink,
    calculateTotalPrice,
    calculateTotalOldPrice,
    calculateCartTotalQuantity,
    calculateGrandTotalQuantity,
    containsDetails,
    getItemDetailsHoverText,
    sortShoppingCartsByName,
    sortShoppingCartsByModifiedDateThenName,
    formatUtcDateToLocal
}